<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading'],
  data() {
    return {
      title: "Inventory",
      text: "Inventory",
      text1: "Low Stock",
      data: [],
      total_low_stock_items: 0,
      selectedBrand: null, 
      brands: [],           
    };
  },

  mounted() {
    this.loadBrands();
    this.loadLowStock();
  },

  computed: {
    total_warehouses_affected() {
      return this.data.reduce((acc, item) => acc + item.warehouses.filter(w => w.is_low).length, 0);
    },
    critical_items() {
      return this.data.filter(item => item.warehouses.some(w => w.available <= 0)).length;
    },
  },
  watch: {
  selectedBrand() {
    this.loadLowStock();
  }
},

  methods: {
    async loadLowStock() {
      this.setLoading(true);
      try {
        const params = {}; 
        if (this.selectedBrand) params.brand_id = this.selectedBrand;

        const response = await axios.get('/inventory/stocks/low-stock', { params });
        this.data = response.data.data.items;
        this.total_low_stock_items = response.data.data.total_low_stock_items;
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },

      async loadBrands() {
        try {
          const response = await axios.get('/brands/dropdown');
          this.brands = response.data.data;
        } catch (error) {
          this.$swalToast(error.message, 'error');
        }
      },

    stockPercent(item, wh) {
      if (!item.threshold || item.threshold === 0) return 0;
      return Math.min(100, Math.round((wh.available / item.threshold) * 100));
    },

    stockBarClass(item, wh) {
      const pct = this.stockPercent(item, wh);
      if (pct <= 10) return 'bg-danger';
      if (pct <= 50) return 'bg-warning';
      return 'bg-success';
    },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Breadcrumb -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
      </div>

<!-- Card wrapper -->
<div class="card border-0 shadow-sm mb-4" style="border-radius:14px;">
  <div class="card-header bg-white border-bottom d-flex align-items-center justify-content-between flex-wrap gap-3 py-3 px-4">
    <h5 class="mb-0 fw-bold" style="font-size:14px; color:#0f172a;">
      Low Stock Items
    </h5>
    <!-- Filter -->
    <div class="l-filter-bar">
      <vue-select
        class="l-vselect"
        v-model="selectedBrand"
        :options="brands"
        label="name"
        :reduce="b => b.id"
        placeholder="All Brands"
        :clearable="true"
        style="min-width: 160px;"
      ></vue-select>
    </div>
  </div>
</div>

      <!-- Summary Cards Row -->
      <div class="row mb-4 g-3">
        <div class="col-xl-4 col-md-4 col-sm-6">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body d-flex align-items-center">
              <div class="avatar avatar-lg bg-danger-subtle rounded-circle me-3 d-flex align-items-center justify-content-center flex-shrink-0">
                <i class="ti ti-alert-triangle text-danger"></i>
              </div>
              <div>
                <p class="text-muted small mb-0">Total Low Stock Items</p>
                <h4 class="fw-bold mb-0 text-danger">{{ total_low_stock_items }}</h4>
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-4 col-md-4 col-sm-6">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body d-flex align-items-center">
              <div class="avatar avatar-lg bg-warning-subtle rounded-circle me-3 d-flex align-items-center justify-content-center flex-shrink-0">
                <i class="ti ti-building-warehouse text-warning"></i>
              </div>
              <div>
                <p class="text-muted small mb-0">Warehouses Affected</p>
                <h4 class="fw-bold mb-0 text-warning">{{ total_warehouses_affected }}</h4>
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-4 col-md-4 col-sm-6">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body d-flex align-items-center">
              <div class="avatar avatar-lg bg-danger-subtle rounded-circle me-3 d-flex align-items-center justify-content-center flex-shrink-0">
                <i class="ti ti-circle-x text-danger"></i>
              </div>
              <div>
                <p class="text-muted small mb-0">Out of Stock</p>
                <h4 class="fw-bold mb-0 text-danger">{{ critical_items }}</h4>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Card Grid -->
      <div v-if="data.length">
        <h5 class="mb-3 fw-semibold">Low Stock Items</h5>
        <div class="row g-3 pb-4">
          <div
            v-for="(item, index) in data"
            :key="item.item_id"
            class="col-xl-4 col-md-6 col-12"
          >
            <div class="card border-0 shadow-sm h-100 low-stock-card">
              <!-- Card Header -->
              <div class="card-header border-bottom bg-transparent d-flex align-items-center justify-content-between py-3">
                <div class="d-flex align-items-center gap-2">
                  <div class="avatar avatar-sm bg-danger-subtle rounded-circle d-flex align-items-center justify-content-center">
                    <i class="ti ti-box text-danger"></i>
                  </div>
                  <div>
                    <h6 class="fw-semibold mb-0">{{ item.name }}</h6>
                    <div class="d-flex align-items-center gap-2 mt-1">
                      <span class="badge bg-light text-dark small">{{ item.code }}</span>
                      <span class="badge bg-info-subtle text-info small">{{ item.brand }}</span> 
                    </div>
                  </div>
                </div>
                <span class="badge bg-danger-subtle text-danger rounded-pill">
                  {{ item.warehouses.filter(w => w.is_low).length }} / {{ item.warehouses.length }} low
                </span>
              </div>

              <!-- Warehouse Rows -->
              <div class="card-body p-0">
                <div
                  v-for="(wh, wIdx) in item.warehouses"
                  :key="wh.id"
                  class="warehouse-row px-3 py-2"
                  :class="{ 'border-top': wIdx > 0 }"
                >
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <span class="small fw-medium text-dark">
                      <i class="ti ti-building-warehouse me-1 text-muted"></i>{{ wh.name }}
                    </span>
                    <span v-if="wh.is_low" class="badge bg-danger small">
                      <i class="ti ti-alert-triangle me-1"></i>Low Stock
                    </span>
                    <span v-else class="badge bg-success small">OK</span>
                  </div>

                  <!-- Progress Bar -->
                  <div
                    class="progress-bar mb-2"
                    :class="stockBarClass(item, wh)"
                    :style="{ width: stockPercent(item, wh) + '%' }"
                  ></div>

                  <p style="font-size: 11px; " :class="wh.is_low ? 'text-danger' : 'text-success'">
                    {{ stockPercent(item, wh) }}% of threshold
                    ({{ wh.available }} / {{ item.threshold }})
                  </p>

                  <!-- Stats Row -->
                  <div class="d-flex gap-3">
                    <div class="text-center">
                      <p class="text-muted mb-0" style="font-size: 10px;">On Hand</p>
                      <span class="fw-semibold small">{{ wh.qty_on_hand }}</span>
                    </div>
                    <div class="text-center">
                      <p class="text-muted mb-0" style="font-size: 10px;">Reserved</p>
                      <span class="fw-semibold small text-warning">{{ wh.reserved }}</span>
                    </div>
                    <div class="text-center">
                      <p class="text-muted mb-0" style="font-size: 10px;">Available</p>
                      <span class="fw-semibold small" :class="wh.is_low ? 'text-danger' : 'text-success'">
                        {{ wh.available }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="card border-0 shadow-sm">
        <div class="card-body p-5 text-center text-muted">
          <i class="ti ti-mood-happy fs-1 d-block mb-2 text-success"></i>
          <h6 class="fw-semibold text-success mb-1">All Good!</h6>
          <p class="mb-0 small">No low stock items found.</p>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>
</template>

<style scoped>
.low-stock-card {
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.low-stock-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1) !important;
}
.warehouse-row {
  background: transparent;
  transition: background 0.1s;
}
.warehouse-row:hover {
  background: #f8f9fa;
}
:deep(.l-vselect .vs__dropdown-toggle) {
  border: 1.5px solid #e2e8f0 !important;
  border-radius: 8px !important;
  min-height: 36px !important;
  padding: 0 8px !important;
  background: #fff !important;
}

:deep(.l-vselect .vs__dropdown-toggle:focus-within) {
  border-color: #6366f1 !important;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, .1) !important;
}

:deep(.l-vselect .vs__search) {
  font-size: 12.5px !important;
}

:deep(.l-vselect .vs__selected) {
  font-size: 12px !important;
  background: #eef2ff !important;
  border: 1px solid #c7d2fe !important;
  border-radius: 5px !important;
  color: #3730a3 !important;
}
</style>
